import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import authReducer from "./_features/auth/state/authSlice";
import dataReducer from "./_filtering/state/dataSlice";
import organisationReducer from "./_features/accountSettings/state/slice";
import paymentGatewayReducer from "./_features/paymentGateway/state/slice";
import subscriptionsFeatureReducer from "./_features/subscriptions/state/slice";
import invoicesFeatureReducer from "./_features/invoices/state/slice";
import paymentsFeatureReducer from "./_features/payments/state/slice";
import billingLogicReducer from "./_features/billingLogic/state/slice";
import integrationReducer from "./_features/integrations/state/slice";
import selfServeCheckoutReducer from "./_features/selfServeUsers/state/slice";
import upsellDownsellReducer from "./_features/upsellDownsell/state/slice";
import { apiSlice } from "./_api/apiSlice";
import { withOrgScopedFilterReset } from "./_app/organisationChanged";
import networkReducer from "./_api/networkSlice";
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import companyFeatureReducer from "./_features/company/state/slice";
import billingUnitsFeatureReducer from "./_features/billingUnits/state/slice";
import systemFeaturesFeatureReducer from "./_features/systemFeatures/state/slice";
import marketsFeatureReducer from "./_features/markets/state/slice";
import proposalFeatureReducer from "./_features/proposals/state/slice";
import productsFeatureReducer from "./_features/products/state/slice";
import usageSourcesFeatureReducer from "./_features/usageSources/state/slice";
import userFeatureReducer from "./_features/userManagement/state/slice";
import customerPortalReducer from "./_features/pay/state/customerPortalSlice";
import objectCustomizationFeatureReducer from "./_features/objectCustomization/state/slice";
import usageCompilationFeatureReducer from "./_features/usageCompilation/state/slice";
import plansFeatureReducer from "./_features/plans/state/slice";
import importsFeatureReducer from "./_features/imports/state/slice";
import permissionReducer from "./_features/permissions/state/slice";
import contactsFeatureReducer from "./_features/contacts/state/slice";
import navigationReducer from "./_layouts/navigation/state/navigationSlice";
import { exportApi } from "./_api/exportApiSlice";
import exportFeatureReducer from "./_features/exports/state/slice";
import rolesFeatureReducer from "./_features/roles/state/slice";
import usageCollectionFeatureReducer from "./_features/usageCollection/state/slice";
// Import wallet API slice to ensure it's injected into the main API slice
import "./_features/pay/api/walletApiSlice";

// `permissionBypass` must never come from storage - `authSlice`'s own
// initialState already derives it fresh from REACT_APP_PERMISSION_BYPASS on
// every load, but redux-persist's rehydrate otherwise overwrites that with
// whatever was cached from a PREVIOUS session's env value. Once bypass was
// ever `true` on a given browser, it stayed stuck `true` forever after -
// surviving env var changes, dev server restarts, and even sign-out (which
// explicitly preserves it) - because nothing ever cleared the localStorage
// copy. Stripping it on the way out of storage means every load re-derives
// it from the current env var, the way the initialState already intends.
const authTransform = createTransform(
  (inboundState) => inboundState,
  (outboundState) => ({
    ...outboundState,
    permissionBypass: process.env.REACT_APP_PERMISSION_BYPASS === "true",
  }),
  { whitelist: ["auth"] }
);

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  // exportApi is a separate createApi() instance from the main apiSlice
  // (own reducerPath, line ~86 below) - it was missing from this blacklist,
  // so its RTK Query cache (including getExports list results) got
  // persisted to localStorage and rehydrated as already-fulfilled on every
  // fresh load. That meant /settings/exports never actually refetched -
  // it just kept showing whatever was cached the last time the tab was
  // open, silently stale, with no network request to notice was missing.
  // "network" is excluded for the same reason: a persisted
  // isServerReachable:false would rehydrate a phantom offline banner on
  // the next cold load with no request in flight to correct it.
  blacklist: [apiSlice.reducerPath, exportApi.reducerPath, "network"],
  transforms: [authTransform],
};

const rootReducer = combineReducers({
  companyFeature: companyFeatureReducer,
  billingUnitsFeature: billingUnitsFeatureReducer,
  proposalFeature: proposalFeatureReducer,
  productsFeature: productsFeatureReducer,
  usageSourcesFeature: usageSourcesFeatureReducer,
  userFeature: userFeatureReducer,
  exportFeature: exportFeatureReducer,
  auth: authReducer,
  data: dataReducer,
  organisation: organisationReducer,
  paymentGateway: paymentGatewayReducer,
  systemFeaturesFeature: systemFeaturesFeatureReducer,
  marketsFeature: marketsFeatureReducer,
  subscriptionsFeature: subscriptionsFeatureReducer,
  billingLogic: billingLogicReducer,
  invoicesFeature: invoicesFeatureReducer,
  paymentsFeature: paymentsFeatureReducer,
  integration: integrationReducer,
  selfServeCheckout: selfServeCheckoutReducer,
  upsellDownsell: upsellDownsellReducer,
  customerPortal: customerPortalReducer,
  objectCustomizationFeature: objectCustomizationFeatureReducer,
  usageCompilationFeature: usageCompilationFeatureReducer,
  plansFeature: plansFeatureReducer,
  importsFeature: importsFeatureReducer,
  permission: permissionReducer,
  contactsFeature: contactsFeatureReducer,
  navigation: navigationReducer,
  rolesFeature: rolesFeatureReducer,
  usageCollectionFeature: usageCollectionFeatureReducer,
  network: networkReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [exportApi.reducerPath]: exportApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, withOrgScopedFilterReset(rootReducer));

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
      immutableCheck: false,
    }).concat(apiSlice.middleware, exportApi.middleware),
  devtools: true,
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);
